import { useEffect, useState } from "react";
import axios from "axios";
import { Trophy, Coins, Brain, Grid3X3, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  {
    key: "top_slots",
    title: "Top Slot Hitters",
    icon: <Coins size={14} />,
    accent: "border-[#D4AF37] text-[#D4AF37]",
    subtitle: "Highest slot payouts this week",
  },
  {
    key: "speed_demons",
    title: "Speed Demons",
    icon: <Brain size={14} />,
    accent: "border-purple-500 text-purple-400",
    subtitle: "Fastest memory clear this week",
  },
  {
    key: "blackouts",
    title: "Bingo Blackouts",
    icon: <Grid3X3 size={14} />,
    accent: "border-red-600 text-red-400",
    subtitle: "Most lines cleared this week",
  },
];

const RANK_STYLES = [
  { chip: "bg-[#D4AF37] text-black", border: "border-[#D4AF37]", glow: "shadow-[0_0_24px_-6px_#D4AF37]" },
  { chip: "bg-neutral-300 text-black", border: "border-neutral-400", glow: "" },
  { chip: "bg-amber-700 text-black",   border: "border-amber-700",   glow: "" },
];

export default function ArcadeLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/arcade/leaderboard`);
        if (!cancelled) setData(data);
      } catch {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-[#0F0F0F] border border-zinc-800 p-6 flex items-center justify-center min-h-[200px]">
        <Loader2 className="animate-spin text-[#D4AF37]" />
      </div>
    );
  }
  if (!data) return null;

  const formatScore = (row, key) => {
    if (key === "top_slots") return `${row.score} Phoenix Tokens`;
    if (key === "speed_demons") return `${row.score.toFixed(1)}s`;
    if (key === "blackouts") {
      return `${row.score} line${row.score !== 1 ? "s" : ""}${row.full ? " · BLACKOUT" : ""}`;
    }
    return row.score;
  };

  return (
    <div
      data-testid="arcade-leaderboard"
      className="bg-[#0F0F0F] border border-zinc-800 p-6 mb-6"
    >
      <div className="text-center mb-5">
        <div className="inline-flex items-center gap-2 px-3 py-1 border border-[#D4AF37]/40 text-[#D4AF37] mb-2">
          <Trophy size={12} />
          <span className="font-accent text-[10px] tracking-[0.3em] uppercase">
            Arcade Leaderboards
          </span>
        </div>
        <h3 className="font-display text-2xl text-white">
          Champions of the Week
        </h3>
        <p className="text-xs text-neutral-500 mt-1">
          Resets every Monday 00:00 PT
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {CATEGORIES.map((cat) => {
          const rows = (data[cat.key] || []).slice(0, 3);
          return (
            <div
              key={cat.key}
              data-testid={`arcade-board-${cat.key}`}
              className="bg-[#0A0A0A] border border-zinc-900 p-4"
            >
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 border ${cat.accent} text-[10px] uppercase tracking-widest font-bold mb-2`}>
                {cat.icon}
                {cat.title}
              </div>
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider mb-3">
                {cat.subtitle}
              </p>
              {rows.length === 0 ? (
                <p className="text-xs text-neutral-600 text-center py-6">
                  No champions yet. Play to claim it.
                </p>
              ) : (
                <ol className="space-y-1.5">
                  {rows.map((row, i) => {
                    const meta = RANK_STYLES[i] || RANK_STYLES[2];
                    return (
                      <li
                        key={row.user_id}
                        data-testid={`arcade-board-${cat.key}-${i + 1}`}
                        className="flex items-center gap-2"
                      >
                        <span
                          className={`w-7 h-7 ${meta.chip} flex items-center justify-center text-xs font-bold ${meta.glow}`}
                        >
                          {i + 1}
                        </span>
                        {row.picture ? (
                          <img
                            src={row.picture}
                            alt={row.name}
                            className={`w-7 h-7 object-contain bg-black border ${meta.border}`}
                          />
                        ) : (
                          <div className={`w-7 h-7 bg-[#1a0d2b] border ${meta.border} flex items-center justify-center text-xs font-display text-[#D4AF37]`}>
                            {(row.name || "?").charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate font-bold">
                            {row.name}
                            {row.is_owner && <span className="ml-1 text-[9px] text-[#D4AF37]">👑</span>}
                          </div>
                          <div className="text-[10px] text-[#D4AF37] font-bold">
                            {formatScore(row, cat.key)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
